import React from 'react';
import {
    all,
    call,
    delay,
    fork,
    put,
    race,
    select,
    take,
    takeEvery,
    takeLatest
} from 'redux-saga/effects';
import keyBy from 'lodash.keyby';
import set from 'lodash.set';
import get from 'lodash.get';
import { AxiosResponse, AxiosError } from 'axios';
import moment from 'moment';
// @ts-ignore
import { REHYDRATE } from 'redux-persist';

// Global Types
import { SagaIterator } from 'redux-saga';
import { JobTypeGroupedUserAssignment } from 'services/userEntityAssignments/types';

// Global Components
// @ts-ignore
import EventQuickUpdateCancelButton from 'components/EventQuickUpdateCancelButton';

// Global Actions
// @ts-ignore
import { updateQueryString } from 'services/router/actions';
// @ts-ignore
import { enqueueSnackbar, closeSnackbar } from 'services/notifications/actions';

// Global Constants
// @ts-ignore
import { SET_IS_ONLINE } from 'services/connection/constants';

// Global Utils
// @ts-ignore
import axiosPoll from 'utils/axiosPoll';
import { takeLatestWithCancel, putRequest } from 'utils/redux-saga';

// Global Selectors
// @ts-ignore
import { selectIsOnline } from 'services/connection/selectors';
import { selectEntityDefaultJobs } from 'services/userEntityAssignments/selectors';

// Local Types
import {
    HarvestEventGroupId,
    HarvestEvent,
    HarvestEventValueUpdateAction,
    HarvestEventsValueUpdateAction,
    HarvestEventTimelineUpdateAction
} from './types';

// Local Actions
import {
    succeededLoadHarvestEvents,
    failedLoadHarvestEvents,
    requestSaveHarvestEvents,
    succeededSaveHarvestEvents,
    failedSaveHarvestEvents,
    updateHarvestEvent,
    updateHarvestEvents,
    setHarvestEvents,
    mergeHarvestEvents,
    deleteHarvestEvents,
    setHarvestEventErrors
} from './actions';

// Local Selectors
import { selectHarvestEventsLoadParams } from './params';
import {
    selectUnsavedHarvestEvents,
    selectHarvestEvents,
    makeSelectHarvestEvent
} from './selectors';

// Local Constants
import {
    HARVEST_EVENTS_LOAD_REQUESTED,
    HARVEST_EVENTS_LOAD_CANCELED,
    HARVEST_EVENTS_SAVE_REQUESTED,
    HARVEST_EVENT_UPDATE,
    HARVEST_EVENTS_UPDATE,
    HARVEST_EVENT_VALUE_UPDATE,
    HARVEST_EVENTS_VALUE_UPDATE,
    HARVEST_EVENT_REMOVE,
    HARVEST_EVENT_TIMELINE_UPDATE,
    HARVEST_EVENT_TIMELINE_UNDO
} from './constants';

// Local Utils
import { formatQuantitySchedules } from './utils';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* loadHarvestEventsSucceeded(
    response: AxiosResponse
): SagaIterator {
    const { data } = response;

    const harvestEvents = data.map((harvestEvent: HarvestEvent) => ({
        ...harvestEvent,
        quantitySchedules: formatQuantitySchedules(harvestEvent)
    }));

    const harvestEventsKeyedByUuid = keyBy(harvestEvents, 'uuid');

    yield put(setHarvestEvents(harvestEventsKeyedByUuid));

    yield put(succeededLoadHarvestEvents(response));
}

export function* loadHarvestEventsFailed(error: AxiosError): SagaIterator {
    yield put(failedLoadHarvestEvents(error));
}

export function* loadHarvestEvents(): SagaIterator {
    const params = yield select(selectHarvestEventsLoadParams);

    // TODO: create polling saga that polls for changes sence a given date stamp.
    if (params) {
        yield call(axiosPoll, {
            config: {
                method: 'GET',
                // Use #harvest to namespace hash storage
                url: `${REACT_APP_API_URL}/v1/Events#harvest`,
                params
            },
            succeeded: loadHarvestEventsSucceeded,
            failed: loadHarvestEventsFailed,
            persistOffline: true
        });
    }
}

export function* watchHarvestEventsLoadRequested(): SagaIterator {
    yield takeLatestWithCancel(
        HARVEST_EVENTS_LOAD_REQUESTED,
        HARVEST_EVENTS_LOAD_CANCELED,
        loadHarvestEvents
    );
}

export function* saveHarvestEvents(): SagaIterator {
    yield delay(1000);

    const unsavedHarvestEvents = yield select(selectUnsavedHarvestEvents);

    try {
        const response = yield call(
            putRequest,
            'Events/bulk',
            unsavedHarvestEvents
        );

        yield put(succeededSaveHarvestEvents(response));

        const deletedHarvestEvents = response.data.delete;
        const createdHarvestEvents = response.data.create;
        const updatedHarvestEvents = response.data.update;
        const errors = get(response, ['data', 'errors'], []);

        if (deletedHarvestEvents) {
            const deletedHarvestEventUuids = deletedHarvestEvents.map(
                (value: HarvestEvent) => value.uuid
            );

            yield put(deleteHarvestEvents(deletedHarvestEventUuids));
        }

        if (createdHarvestEvents) {
            const createdHarvestEventsKeyedByUuid = keyBy(
                createdHarvestEvents,
                'uuid'
            );

            yield put(mergeHarvestEvents(createdHarvestEventsKeyedByUuid));
        }

        if (updatedHarvestEvents) {
            const updatedHarvestEventsKeyedByUuid = keyBy(
                updatedHarvestEvents,
                'uuid'
            );

            yield put(mergeHarvestEvents(updatedHarvestEventsKeyedByUuid));
        }

        yield put(setHarvestEventErrors(errors));

        if (errors.length) {
            yield put(failedSaveHarvestEvents(response));

            yield put(
                updateQueryString({
                    error: true
                })
            );
        }
    } catch (error) {
        yield put(failedSaveHarvestEvents(error));

        yield put(
            updateQueryString({
                error: true
            })
        );
    }
}

export function* watchHarvestEventsSaveRequested(): SagaIterator {
    yield takeLatest(HARVEST_EVENTS_SAVE_REQUESTED, saveHarvestEvents);
}

export function* triggerSaveHarvestEvents(): SagaIterator {
    const isOnline = yield select(selectIsOnline);
    const unsavedHarvestEvents = yield select(selectUnsavedHarvestEvents);

    if (isOnline && unsavedHarvestEvents) {
        yield put(requestSaveHarvestEvents());
    }
}

/**
 * watch for any actions that we want to trigger a save
 */
export function* watchHarvestEventsSaveTriggers(): SagaIterator {
    yield takeLatest(
        [
            REHYDRATE,
            HARVEST_EVENT_UPDATE,
            HARVEST_EVENTS_UPDATE,
            HARVEST_EVENT_REMOVE,
            SET_IS_ONLINE
        ],
        triggerSaveHarvestEvents
    );
}

export function* setValueOnHarvestEvent(
    action: HarvestEventValueUpdateAction
): SagaIterator {
    const {
        payload: { harvestEventUuid, field, value }
    } = action;

    const harvestEvent = yield select(makeSelectHarvestEvent(), {
        harvestEventUuid
    });

    // Create a deep clone of the harvestEvent
    const nextHarvestEvent = JSON.parse(JSON.stringify(harvestEvent));

    set(nextHarvestEvent, field, value);

    yield put(updateHarvestEvent(nextHarvestEvent));
}

export function* watchHarvestEventValueUpdate(): SagaIterator {
    yield takeEvery(HARVEST_EVENT_VALUE_UPDATE, setValueOnHarvestEvent);
}

export function* setValueOnHarvestEvents(
    action: HarvestEventsValueUpdateAction
): SagaIterator {
    const {
        payload: { harvestEventUuids, field, value }
    } = action;

    const harvestEvents = yield select(selectHarvestEvents);

    const updatedHarvestEvents = harvestEventUuids.map((uuid) => {
        const harvestEvent = JSON.parse(JSON.stringify(harvestEvents[uuid]));

        set(harvestEvent, field, value);

        return harvestEvent;
    });

    yield put(updateHarvestEvents(updatedHarvestEvents));
}

export function* watchHarvestEventsValueUpdate(): SagaIterator {
    yield takeEvery(HARVEST_EVENTS_VALUE_UPDATE, setValueOnHarvestEvents);
}

export function* updateHarvestEventTimeline(
    action: HarvestEventTimelineUpdateAction
): SagaIterator {
    const {
        payload: { harvestEventUuid, previousGroupId, nextGroupId, date }
    } = action;

    const momentDate = moment(date, 'YYYY-MM-DD');

    const harvestEvent = yield select(makeSelectHarvestEvent(), {
        harvestEventUuid
    });

    if (harvestEvent) {
        let defaultJobs: JobTypeGroupedUserAssignment[] = [];

        // @ts-ignore
        defaultJobs = yield select(selectEntityDefaultJobs, {
            entityId: harvestEvent.entityId
        });

        // Create a deep clone of the harvestEvent
        const nextHarvestEvent: HarvestEvent = JSON.parse(
            JSON.stringify(harvestEvent)
        );

        if (defaultJobs && defaultJobs.length) {
            const assignedUsersInspection = get(
                nextHarvestEvent,
                ['jobs', 0, 'assignedUsers'],
                []
            );
            const assignedUsersTimesheet = get(
                nextHarvestEvent,
                ['jobs', 1, 'assignedUsers'],
                []
            );

            const defaultAssignedUsersEmailsInspection = defaultJobs[0].assignedUsers.map(
                (email: string) => ({ email })
            );
            const defaultAssignedUsersEmailsTimesheet = defaultJobs[1].assignedUsers.map(
                (email: string) => ({ email })
            );

            if (!assignedUsersInspection.length) {
                set(
                    nextHarvestEvent,
                    'jobs[0].assignedUsers',
                    defaultAssignedUsersEmailsInspection
                );
            }

            if (!assignedUsersTimesheet.length) {
                set(
                    nextHarvestEvent,
                    'jobs[1].assignedUsers',
                    defaultAssignedUsersEmailsTimesheet
                );
            }
        }

        const {
            quantitySchedules,
            forecastedQuantity,
            groupIds = []
        } = nextHarvestEvent;

        const timestamp = moment.utc().format();

        if (quantitySchedules && quantitySchedules.length) {
            if (date) {
                const nextQuantitySchedules = quantitySchedules.map(
                    (quantitySchedule) => {
                        const nextQuantitySchedule = {
                            date: momentDate.format('YYYY-MM-DD'),
                            value: quantitySchedule.value
                        };

                        momentDate.add(1, 'day');
                        return nextQuantitySchedule;
                    }
                );

                set(
                    nextHarvestEvent,
                    'quantitySchedules',
                    nextQuantitySchedules
                );
            }
            if (!date) {
                // Unschedule
                set(nextHarvestEvent, 'quantitySchedules', []);
                set(nextHarvestEvent, 'jobs[0].assignedUsers', []);
                set(nextHarvestEvent, 'jobs[1].assignedUsers', []);
            }
        } else {
            set(nextHarvestEvent, ['quantitySchedules', 0], {
                date: momentDate.format('YYYY-MM-DD'),
                value: forecastedQuantity || 0
            });
        }

        if (previousGroupId && nextGroupId) {
            const nextGroupIds = groupIds.filter(
                (groupId: HarvestEventGroupId) =>
                    groupId !== previousGroupId && groupId !== nextGroupId
            );
            nextGroupIds.push(nextGroupId);
            set(nextHarvestEvent, 'groupIds', nextGroupIds);
        }
        if (previousGroupId && !nextGroupId) {
            const nextGroupIds = groupIds.filter(
                (groupId: HarvestEventGroupId) => groupId !== previousGroupId
            );
            set(nextHarvestEvent, 'groupIds', nextGroupIds);
        }
        if (!previousGroupId && nextGroupId) {
            set(nextHarvestEvent, 'groupIds', [nextGroupId]);
        }
        if (!previousGroupId && !nextGroupId) {
            set(nextHarvestEvent, 'groupIds', []);
        }

        set(nextHarvestEvent, 'orderedAt', timestamp);

        yield put(updateHarvestEvent(nextHarvestEvent));

        // Enqueue Snackbar
        const { payload } = yield put(
            enqueueSnackbar({
                message: 'Item updated',
                options: {
                    variant: 'default',
                    action: <EventQuickUpdateCancelButton />,
                    autoHideDuration: undefined
                }
            })
        );

        const notificationKey = get(payload, ['notification', 'key']);

        const { undo, timeout } = yield race({
            undo: take(HARVEST_EVENT_TIMELINE_UNDO),
            timeout: delay(6000),
            next: take(HARVEST_EVENT_TIMELINE_UPDATE)
        });

        if (undo) {
            yield put(updateHarvestEvent(harvestEvent));
        }
        if (undo || timeout) {
            yield put(closeSnackbar(notificationKey));
        }
    }
}

export function* watchHarvestEventTimelineUpdate(): SagaIterator {
    yield takeEvery(HARVEST_EVENT_TIMELINE_UPDATE, updateHarvestEventTimeline);
}

export default function* harvestEventsSagas(): SagaIterator {
    yield all([
        fork(watchHarvestEventsLoadRequested),
        fork(watchHarvestEventsSaveRequested),
        fork(watchHarvestEventsSaveTriggers),
        fork(watchHarvestEventValueUpdate),
        fork(watchHarvestEventsValueUpdate),
        fork(watchHarvestEventTimelineUpdate)
    ]);
}
